import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Building2, Users, FileText, Mail, Phone, Loader2 } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { DataTable, Column } from '../components/common/DataTable';
import { RestrictionBadge } from '../components/common/RestrictionBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Company, RestrictionLevel } from '../types';
import { getCompaniesPaginated } from '../api-clients';
import { CompanyDto } from '../web-api-client';
import { PaginationControls } from '../components/common/PaginationControls';
import { useToast } from '../hooks/use-toast';
import { getErrorToast } from '../lib/error-utils';

// Helper function to map CarrierDto to Company
function mapCompanyDtoToCompany(company: CompanyDto): Company {
  return {
    id: company.id?.toString() || '',
    name: company.companyName || '',
    vatNumber: company.vatNumber || '',
    address: '', // Not available in CompanyDto
    phone: company.contactPhone || '',
    email: company.contactEmail || '',
    employeeCount: company.employeeCount || 0,
    ecmrCount: company.documentCount || 0,
    createdAt: company.created ? new Date(company.created).toISOString() : new Date().toISOString(),
    status: company.isActive ? 'active' : 'inactive',
    restrictionLevel: (company.restrictionLevel ?? 0) as RestrictionLevel,
  };
}

export default function Companies() {
    const navigate = useNavigate();
    const { toast } = useToast();
    const [companies, setCompanies] = useState<Company[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [pageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        loadCompanies();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, searchTerm]);

    const loadCompanies = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const data = await getCompaniesPaginated({
                page: currentPage,
                pageSize: pageSize,
                searchTerm: searchTerm || undefined,
            });
            
            const mappedCompanies = (data.items || []).map(mapCompanyDtoToCompany);
            
            setCompanies(mappedCompanies);
            setTotalPages(data.metadata?.totalPages || 1);
            setTotalCount(data.metadata?.totalCount || 0);
        } catch (err) {
            const errorToast = getErrorToast(err, 'Erro ao carregar empresas', 'Failed to load companies');
            setError(errorToast.description);
            toast({
                title: errorToast.title,
                description: errorToast.description,
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const columns: Column<Company>[] = [
        {
            key: 'name',
            header: 'Empresa',
            render: (company) => (
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                        <Building2 className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                        <p className="font-medium">{company.name}</p>
                        <p className="text-sm text-muted-foreground">{company.vatNumber}</p>
                    </div>
                </div>
            ),
        },
        {
            key: 'location',
            header: 'Localização',
            render: (company) => (
                <div>
                    <p className="text-sm text-muted-foreground">{company.address || 'N/A'}</p>
                </div>
            ),
        },
        {
            key: 'contact',
            header: 'Contacto',
            render: (company) => (
                <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                        <Mail className="h-3 w-3 text-muted-foreground" />
                        <span>{company.email}</span>
                    </div>
                    {company.phone && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Phone className="h-3 w-3" />
                            <span>{company.phone}</span>
                        </div>
                    )}
                </div>
            ),
        },
        {
            key: 'employees',
            header: 'Colaboradores',
            render: (company) => (
                <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span>{company.employeeCount}</span>
                </div>
            ),
        },
        {
            key: 'ecmrs',
            header: 'eCMRs',
            render: (company) => (
                <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span>{company.ecmrCount}</span>
                </div>
            ),
        },
        {
            key: 'restrictionLevel',
            header: 'Nível de Restrição',
            render: (company) => (
                <RestrictionBadge level={company.restrictionLevel ?? 0} />
            ),
        },
        {
            key: 'actions',
            header: '',
            render: (company) => (
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/companies/${company.id}`);
                    }}
                >
                    Ver Detalhes
                </Button>
            ),
            className: 'text-right',
        },
    ];

    if (loading && companies.length === 0) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title="Empresas"
                description="Gerir empresas de transporte registadas"
                actions={
                    <Button onClick={() => navigate('/companies/new')}>
                        <Plus className="h-4 w-4" />
                        Nova Empresa
                    </Button>
                }
            />

            {error && (
                <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-md">
                    {error}
                </div>
            )}

            <div className="flex items-center gap-4">
                <Input
                    placeholder="Pesquisar empresas..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="max-w-sm"
                />
            </div>

            <DataTable
                columns={columns}
                data={companies}
                onRowClick={(company) => navigate(`/companies/${company.id}`)}
                emptyMessage="Nenhuma empresa encontrada"
            />

            <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                itemsShown={companies.length}
                itemLabel="empresas"
                onPageChange={setCurrentPage}
            />
        </div>
    );
}
